class WishlistManager {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.wishlist = this.getWishlist();
    this.updateAllButtons();
    
    // Initialize wishlist page if we're on it
    if (window.location.pathname.includes('/pages/wishlist')) {
      this.initWishlistPage();
    }
  }

  getWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      // Handle potential nested array structure
      if (stored) {
        const parsed = JSON.parse(stored);
        // If the data is nested in an array, flatten it
        return Array.isArray(parsed[0]) ? parsed[0] : parsed;
      }
      return [];
    } catch (error) {
      console.error('Error getting wishlist:', error);
      return [];
    }
  }

  saveWishlist() {
    try {
      // Store as a simple array, not nested
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      this.updateAllButtons();
      console.log('Current wishlist:', this.wishlist); // Debug log
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  toggleProduct(productId) {
    if (!productId) {
      console.error('No product ID provided');
      return;
    }
    
    try {
      // Convert productId to string for consistent comparison
      productId = productId.toString();
      
      const index = this.wishlist.indexOf(productId);
      if (index === -1) {
        this.wishlist.push(productId);
        console.log(`Added product ${productId} to wishlist`);
      } else {
        this.wishlist.splice(index, 1);
        console.log(`Removed product ${productId} from wishlist`);
      }
      this.saveWishlist();
    } catch (error) {
      console.error('Error toggling product:', error);
    }
  }

  updateAllButtons() {
    document.querySelectorAll('.wishlist-toggle').forEach(button => {
      const productId = button.dataset.productId?.toString();
      if (productId) {
        const isInWishlist = this.wishlist.includes(productId);
        button.classList.toggle('is-active', isInWishlist);
        button.setAttribute('aria-pressed', isInWishlist);
      }
    });
  }

  async initWishlistPage() {
    const wishlistItemsContainer = document.getElementById('wishlist-items');
    const emptyWishlistMessage = document.querySelector('.wishlist-empty');
    
    if (!wishlistItemsContainer) {
      console.error('Wishlist container not found');
      return;
    }

    if (this.wishlist.length === 0) {
      console.log('Wishlist is empty');
      emptyWishlistMessage?.classList.remove('hidden');
      return;
    } else {
      emptyWishlistMessage?.classList.add('hidden');
    }

    try {
      // Fetch all products with pagination
      let allProducts = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(`/products.json?limit=250&page=${page}`);
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        const data = await response.json();
        
        if (data.products.length === 0) {
          hasMore = false;
        } else {
          allProducts = allProducts.concat(data.products);
          page++;
        }
      }

      console.log('Total products fetched:', allProducts.length);
      console.log('Looking for wishlist ID:', this.wishlist[0]);
      
      // Filter products to only those in the wishlist
      const wishlistProducts = allProducts.filter(product => {
        const productMatch = this.wishlist.includes(product.id.toString());
        console.log('Product:', product.id, 'Match:', productMatch);
        return productMatch;
      });

      // If we found any products, render them
      if (wishlistProducts.length > 0) {
        console.log('Found wishlist products:', wishlistProducts);
        emptyWishlistMessage?.classList.add('hidden');
        
        // Create HTML for each product
        const productCards = wishlistProducts.map(product => {
          const variant = product.variants[0];
          console.log('Product:', product);
          console.log('Variant:', variant);
          const image = product.images[0]?.src || '';
          // Convert string prices to numbers and don't divide by 100
          const regularPrice = parseFloat(variant.compare_at_price || variant.price);
          const salePrice = parseFloat(variant.price);
          const onSale = variant.compare_at_price > variant.price;

          return `
            <li class="grid__item">
              <div class="card-wrapper">
                <div class="card">
                  <button 
                    class="wishlist-remove-button"
                    aria-label="Remove from wishlist"
                    data-product-id="${product.id}"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1L11 11M1 11L11 1" stroke="currentColor" stroke-width="2"/>
                    </svg>
                  </button>
                  <div class="card__media">
                    <img
                      src="${image}"
                      alt="${product.title}"
                      loading="lazy"
                    >
                    ${onSale ? '<span class="badge">Sale</span>' : ''}
                  </div>
                  <div class="card__content">
                    <h3 class="card__heading">
                      <a href="/products/${product.handle}">
                        ${product.title}
                      </a>
                    </h3>
                    <div class="price ${onSale ? 'price--on-sale' : ''}">
                      <span class="price-item price-item--regular">
                        £${regularPrice.toFixed(2)}
                      </span>
                      ${onSale ? `
                        <span class="price-item price-item--sale">
                          £${salePrice.toFixed(2)}
                        </span>
                      ` : ''}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          `;
        }).join('');
        
        wishlistItemsContainer.innerHTML = productCards;
      } else {
        console.log('No wishlist products found');
        emptyWishlistMessage?.classList.remove('hidden');
      }
    } catch (error) {
      console.error('Error fetching wishlist products:', error);
      emptyWishlistMessage?.classList.remove('hidden');
    }

    // Add click handler for remove buttons
    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('.wishlist-remove-button');
      if (removeButton) {
        event.preventDefault();
        const productId = removeButton.dataset.productId;
        if (productId) {
          this.toggleProduct(productId);
          // Remove the item's parent li element from the DOM
          const listItem = removeButton.closest('.grid__item');
          if (listItem) {
            listItem.remove();
          }
          // Show empty message if no items left
          if (this.wishlist.length === 0) {
            document.querySelector('.wishlist-empty')?.classList.remove('hidden');
          }
        }
      }
    });
  }
}

customElements.define('wishlist-button', 
  class WishlistButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.handleClick.bind(this));
    }

    connectedCallback() {
      const productId = this.dataset.productId;
      if (productId && window.wishlistManager) {
        const isInWishlist = window.wishlistManager.wishlist.includes(productId.toString());
        this.classList.toggle('is-active', isInWishlist);
        this.setAttribute('aria-pressed', isInWishlist);
      }
    }

    handleClick(event) {
      event.preventDefault();
      const productId = this.dataset.productId;
      if (productId && window.wishlistManager) {
        window.wishlistManager.toggleProduct(productId);
        const isInWishlist = window.wishlistManager.wishlist.includes(productId.toString());
        this.classList.toggle('is-active', isInWishlist);
        this.setAttribute('aria-pressed', isInWishlist);
      }
    }
  }
);

// Initialize wishlist manager only once when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  if (!window.wishlistManager) {
    window.wishlistManager = new WishlistManager();
  }
}); 